/**
 * Site-wide rating selection: prefer Rating 3.0, then 2.1, then 2.0.
 *
 * Why a fallback and not a straight switch to 3.0:
 *  - Rating 3.0 only exists from season 32 onward (that season was reparsed when R3 shipped;
 *    seasons 29-31 have 0% coverage and are not being reparsed).
 *  - Stats that come from the legacy `actual_player_stats` table can never carry 3.0 — that
 *    table has no R3 column at all. Any surface showing a player's "latest season" (the LFT
 *    page, fantasy cards) will therefore always have some rows stuck on 2.1/2.0.
 *
 * So a surface must both pick the best available rating AND be able to say which one it landed
 * on: rendering a 2.0 value under a "Rating 3.0" heading would be wrong, not merely imprecise.
 */

export type RatingKind = "r3" | "r21" | "r2";

export interface PickedRating {
    value: number;
    kind: RatingKind;
}

/** The version string to show the user, e.g. for a "Rating 3.0" label. */
export const RATING_KIND_VERSION: Record<RatingKind, string> = {
    r3: "3.0",
    r21: "2.1",
    r2: "2.0",
};

/**
 * A rating-bearing stats object. Deliberately loose: the API hands these fields back on several
 * differently-shaped DTOs (PlayerStats, PlayerStatsAPI, the fantasy player's `stats`).
 */
export interface RatingSource {
    rating30?: number | null;
    rating21?: number | null;
    rating2?: number | null;
}

/**
 * Picks the best available rating. Returns null when the source has no usable rating at all, so
 * callers render an explicit placeholder instead of a misleading 0.
 *
 * `> 0` rather than a plain null check: the legacy converters default `rating21`/`rating2` to 0
 * when a season predates them, and a 0 there means "not computed", not "played terribly".
 */
export const pickRating = (source: RatingSource | null | undefined): PickedRating | null => {
    if (!source) return null;
    if (source.rating30 != null && source.rating30 > 0) return { value: source.rating30, kind: "r3" };
    if (source.rating21 != null && source.rating21 > 0) return { value: source.rating21, kind: "r21" };
    if (source.rating2 != null && source.rating2 > 0) return { value: source.rating2, kind: "r2" };
    return null;
};
